import {
  retainLeagueAliases,
  bridgeCoworldRoot,
  type AddressEntries,
} from "./league-address-aliases";
import type { LeagueSummary } from "@softmax/observatory-client/types";
import {
  type CoworldGroup,
  defaultLeague,
  leagueRoute,
  slugifyLeagueName,
  groupCoworldLeagues,
  legacyLeagueNames,
} from "./league-catalog";

export type WatchAddresses<T extends LeagueSummary = LeagueSummary> = {
  /** The logical root for the default league, or game/league for a sibling. */
  slugOf(leagueId: string): string | null;
  /** Accepts aliases and falls back to the game root for unknown sibling names. */
  leagueOf(slug: string): T | null;
  /** Current root and explicit league paths, without historical aliases. */
  currentLeagueOf(slug: string): T | null;
  /** Current paths and retained aliases, without the unknown-sibling fallback. */
  exactLeagueOf(slug: string): T | null;
};
function addCoworldAddresses<T extends LeagueSummary>({
  group: { root, members },
  entries,
}: {
  group: CoworldGroup<T>;
  entries: AddressEntries<T>;
}): void {
  const head = defaultLeague(members);

  for (const league of members) {
    const segment = leagueRoute(league).league;
    const path = `${root}/${segment}`;
    entries.byPath.set(path, league);
    entries.byId.set(league.id, league === head ? root : path);
  }

  entries.byPath.set(root, head);

  // Recorded short names take precedence over name-derived aliases at the same visibility.
  for (const league of members) {
    retainLeagueAliases({
      root,
      league,
      aliases: league.short_name_aliases ?? [],
      entries,
    });
  }

  for (const league of members) {
    retainLeagueAliases({
      root,
      league,
      aliases: [slugifyLeagueName(league.name)],
      entries,
    });
  }
}

function normalizePath(slug: string): string {
  return slug.replace(/^\/+|\/+$/g, "").toLowerCase();
}

// These are logical game/league keys. Catalog prefixes belong to the public URL builders.
// Callers supply a stable order with published leagues before private leagues.
export function watchAddresses<T extends LeagueSummary>(
  leagues: readonly T[],
): WatchAddresses<T> {
  const entries: AddressEntries<T> = {
    byId: new Map(),
    byPath: new Map(),
    retained: new Map(),
  };
  const groups = groupCoworldLeagues(leagues);

  for (const group of groups) {
    addCoworldAddresses({ group, entries });
  }

  for (const { root, legacyRoot } of groups) {
    bridgeCoworldRoot({ from: root, to: legacyRoot, entries });
  }

  const legacy = legacyLeagueNames(leagues);
  const exactLeagueOf = (slug: string): T | null => {
    const path = normalizePath(slug);
    return (
      entries.byPath.get(path) ??
      entries.retained.get(path) ??
      legacy.get(path) ??
      null
    );
  };
  return {
    slugOf: (leagueId) => entries.byId.get(leagueId) ?? null,
    leagueOf: (slug) => {
      const path = normalizePath(slug);
      const exact = exactLeagueOf(path);

      if (exact) return exact;

      // Unrecorded old sibling links retain their existing game-front-door fallback.
      const slash = path.indexOf("/");

      if (slash === -1) return null;

      return exactLeagueOf(path.slice(0, slash));
    },
    currentLeagueOf: (slug) => entries.byPath.get(normalizePath(slug)) ?? null,
    exactLeagueOf,
  };
}
